import { format, isValid, parse } from 'date-fns'
import { z } from 'zod'

/**
 * Schemas for the raw SAP extracts (KNA1, KNB1, KNVP, VBAK, VBRK, VBRP, FBL5N, BP credit segments).
 *
 * The upload worker parses each file positionally against the full ordered column list exported
 * here (`import*Headers`), so those arrays MUST match the raw SAP export exactly. The zod schemas
 * only declare the columns we keep — zod strips every other column — and clean the SAP conventions:
 * the literal string "NULL" and all-zero placeholders become null, customer/vendor numbers have
 * their leading zeros stripped, dates are converted from YYYYMMDD to YYYY-MM-DD, and "X" checkboxes
 * become booleans.
 */

const SAP_STRING_BLANKS = new Set(['', 'NULL'])

/** Trims a SAP string field; the literal "NULL" and empty string become null. */
export const sapStringSchema = z
  .string()
  .nullable()
  .default(null)
  .transform((value) => {
    const trimmed = (value ?? '').trim()
    return SAP_STRING_BLANKS.has(trimmed) ? null : trimmed
  })

/** SAP customer/vendor numbers: strip leading zeros; blank/zero placeholders become null. */
export const sapNumberKeySchema = z
  .string()
  .nullable()
  .default(null)
  .transform((value) => {
    const trimmed = (value ?? '').trim().replace(/^0+/, '')
    return trimmed === '' || trimmed === 'NULL' ? null : trimmed
  })

/** SAP checkbox: "X" means true, everything else false. */
export const sapBooleanSchema = z
  .string()
  .nullable()
  .default(null)
  .transform((value) => (value ?? '').trim().toUpperCase() === 'X')

/** SAP date YYYYMMDD -> YYYY-MM-DD; "00000000", "NULL" and empty become null. */
export const sapDateSchema = z
  .string()
  .nullable()
  .default(null)
  .transform((value) => {
    const trimmed = (value ?? '').trim()
    if (trimmed === '' || trimmed === 'NULL' || /^0+$/.test(trimmed)) return null
    if (trimmed.length === 8) {
      const parsed = parse(trimmed, 'yyyyMMdd', new Date())
      return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : null
    }
    const parsed = new Date(`${trimmed}T00:00:00`)
    return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : null
  })

/**
 * Parses a SAP flat-file decimal amount into a canonical numeric string for drizzle `numeric`
 * columns; returns null when blank/`NULL`/non-numeric. The original digit string is preserved (not
 * re-stringified through `Number`) to avoid floating-point precision loss on large amounts.
 *
 * Handles SAP conventions:
 *  - trailing sign for negatives (`"1234.56-"` → `"-1234.56"`) as well as a leading sign
 *  - comma as a thousands separator; the dot is treated as the decimal separator (SAP ECC flat
 *    exports). Revisit if a real extract is found to use comma-as-decimal.
 */
export function parseSapDecimal(value: string | null | undefined): string | null {
  const raw = (value ?? '').trim()
  if (raw === '' || raw === 'NULL') return null

  // Strip thousands separators, then normalize a trailing/leading sign to a single leading one.
  let normalized = raw.replace(/,/g, '')
  const trailingSign = /^([\d.]+)([+-])$/.exec(normalized)
  if (trailingSign) {
    normalized = `${trailingSign[2] === '-' ? '-' : ''}${trailingSign[1]}`
  } else if (normalized.startsWith('+')) {
    normalized = normalized.slice(1)
  }

  return Number.isFinite(Number(normalized)) ? normalized : null
}

/** SAP decimal amount as a cleaned numeric string (for drizzle numeric columns); null when blank. */
export const sapNumericStringSchema = z
  .string()
  .nullable()
  .default(null)
  .transform((value) => parseSapDecimal(value))

/** SAP integer field; null when blank/invalid. */
export const sapIntegerSchema = z
  .string()
  .nullable()
  .default(null)
  .transform((value) => {
    const trimmed = (value ?? '').trim()
    if (trimmed === '' || trimmed === 'NULL') return null
    const parsed = Number.parseInt(trimmed, 10)
    return Number.isNaN(parsed) ? null : parsed
  })

/** SAP date MM/DD/YYYY -> YYYY-MM-DD; blank/"NULL" become null. Used by the FBL5N/BP extracts. */
export const sapUsDateSchema = z
  .string()
  .nullable()
  .default(null)
  .transform((value) => {
    const trimmed = (value ?? '').trim()
    if (trimmed === '' || trimmed === 'NULL' || /^0+$/.test(trimmed)) return null
    const parsed = parse(trimmed, 'MM/dd/yyyy', new Date())
    return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : null
  })

/**
 * Normalizes a SAP icon-encoded status such as `@5B\QCleared@` / `@5C\Qopen@` down to its readable
 * label (`Cleared` / `open`). The wrapper is `@<code>\Q<label>@`; anything that does not match is
 * returned trimmed as-is. Blank/"NULL" become null.
 */
export const sapIconLabelSchema = z
  .string()
  .nullable()
  .default(null)
  .transform((value) => {
    const trimmed = (value ?? '').trim()
    if (trimmed === '' || trimmed === 'NULL') return null
    const match = /^@[^\\@]*\\Q(.+?)@$/.exec(trimmed)
    const label = match?.[1]?.trim()
    return label ? label : trimmed
  })

/* ------------------------------------------------------------------ */
/* KNA1 — General Customer Master Data                                 */
/* ------------------------------------------------------------------ */

export const importSapCustomerHeaders = [
  'HK_H_ECC_KNA1',
  'MANDT',
  'KUNNR',
  'FILE_ROW_NUMBER',
  'CDC_TIMESTAMP',
  'LAND1',
  'NAME1',
  'NAME2',
  'ORT01',
  'PSTLZ',
  'REGIO',
  'SORTL',
  'STRAS',
  'TELF1',
  'TELFX',
  'XCPDK',
  'ADRNR',
  'MCOD1',
  'MCOD2',
  'MCOD3',
  'ANRED',
  'AUFSD',
  'BAHNE',
  'BAHNS',
  'BBBNR',
  'BBSNR',
  'BEGRU',
  'BRSCH',
  'BUBKZ',
  'DATLT',
  'ERDAT',
  'ERNAM',
  'EXABL',
  'FAKSD',
  'FISKN',
  'KNAZK',
  'KNRZA',
  'KONZS',
  'KTOKD',
  'KUKLA',
  'LIFNR',
  'LIFSD',
  'LOCCO',
  'LOEVM',
  'NAME3',
  'NAME4',
  'NIELS',
  'ORT02',
  'PFACH',
  'PSTL2',
  'COUNC',
  'CITYC',
  'RPMKR',
  'SPERR',
  'SPRAS',
  'STCD1',
  'STCD2',
  'STKZA',
  'STKZU',
  'TELBX',
  'TELF2',
  'TELTX',
  'TELX1',
  'LZONE',
  'XZEMP',
  'VBUND',
  'STCEG',
  'DEAR1',
  'DEAR2',
  'DEAR3',
  'DEAR4',
  'DEAR5',
  'GFORM',
  'BRAN1',
  'BRAN2',
  'BRAN3',
  'BRAN4',
  'BRAN5',
  'EKONT',
  'UMSAT',
  'UMJAH',
  'UWAER',
  'JMZAH',
  'JMJAH',
  'KATR1',
  'KATR2',
  'KATR3',
  'KATR4',
  'KATR5',
  'KATR6',
  'KATR7',
  'KATR8',
  'KATR9',
  'KATR10',
  'STKZN',
  'UMSA1',
  'TXJCD',
  'PERIV',
  'ABRVW',
  'INSPBYDEBI',
  'INSPATDEBI',
  'KTOCD',
  'PFORT',
  'WERKS',
  'DTAMS',
  'DTAWS',
  'DUEFL',
  'HZUOR',
  'SPERZ',
  'ETIKG',
  'CIVVE',
  'MILVE',
  'KDKG1',
  'KDKG2',
  'KDKG3',
  'KDKG4',
  'KDKG5',
  'XKNZA',
  'FITYP',
  'STCDT',
  'STCD3',
  'STCD4',
  'STCD5',
  'XICMS',
  'XXIPI',
  'XSUBT',
  'CFOPC',
  'TXLW1',
  'TXLW2',
  'CCC01',
  'CCC02',
  'CCC03',
  'CCC04',
  'CASSD',
  'KNURL',
  'J_1KFREPRE',
  'J_1KFTBUS',
  'J_1KFTIND',
  'CONFS',
  'UPDAT',
  'UPTIM',
  'NODEL',
  'DEAR6',
  'SUFRAMA',
  'RG',
  'EXP',
  'UF',
  'RGDATE',
  'RIC',
  'RNE',
  'RNEDATE',
  'CNAE',
  'LEGALNAT',
  'CRTN',
  'ICMSTAXPAY',
  'INDTYP',
  'TDT',
  'COMSIZE',
  'DECREGPC',
  '_VSO_R_PALHGT',
  '_VSO_R_PAL_UL',
  '_VSO_R_PK_MAT',
  '_VSO_R_MATPAL',
  '_VSO_R_I_NO_LYR',
  '_VSO_R_ONE_MAT',
  '_VSO_R_ONE_SORT',
  '_VSO_R_ULD_SIDE',
  '_VSO_R_LOAD_PREF',
  '_VSO_R_DPOINT',
  'ALC',
  'PMT_OFFICE',
  'PSOFG',
  'PSOIS',
  'PSON1',
  'PSON2',
  'PSON3',
  'PSOVN',
  'PSOTL',
  'PSOHS',
  'PSOST',
  'PSOO1',
  'PSOO2',
  'PSOO3',
  'PSOO4',
  'PSOO5',
  'ERDAT_SIMP_DT',
  'RGDATE_SIMP_DT',
  'RNEDATE_SIMP_DT',
  'UPDAT_SIMP_DT',
  'UPTIM_SIMP_TM',
  'DB_CUID',
  'DSS_CHANGE_HASH',
  'FILE_NAME',
  'INSERT_TS',
  'EFFECTIVE_START_DATE',
  'DSS_VERSION',
  'UPDATE_TS',
] as const

export const importSapCustomerSchema = z.object({
  KUNNR: sapNumberKeySchema,
  MANDT: sapStringSchema,
  NAME1: sapStringSchema,
  NAME2: sapStringSchema,
  NAME4: sapStringSchema,
  STRAS: sapStringSchema,
  ORT01: sapStringSchema,
  ORT02: sapStringSchema,
  REGIO: sapStringSchema,
  PSTLZ: sapStringSchema,
  LAND1: sapStringSchema,
  SORTL: sapStringSchema,
  TELF1: sapStringSchema,
  TELF2: sapStringSchema,
  TELFX: sapStringSchema,
  ADRNR: sapStringSchema,
  KTOKD: sapStringSchema,
  KUKLA: sapStringSchema,
  BRSCH: sapStringSchema,
  SPRAS: sapStringSchema,
  LIFNR: sapNumberKeySchema,
  AUFSD: sapStringSchema,
  BEGRU: sapStringSchema,
  STCD1: sapStringSchema,
  STCD3: sapStringSchema,
  LZONE: sapStringSchema,
  TXJCD: sapStringSchema,
  ERDAT: sapDateSchema,
  ERNAM: sapStringSchema,
})
export type ImportSapCustomerRecord = z.infer<typeof importSapCustomerSchema>

/* ------------------------------------------------------------------ */
/* KNB1 — Customer Master per Company Code                             */
/* ------------------------------------------------------------------ */

export const importSapCustomerCompanyHeaders = [
  'MANDT',
  'KUNNR',
  'BUKRS',
  'PERNR',
  'ERDAT',
  'ERNAM',
  'SPERR',
  'LOEVM',
  'ZUAWA',
  'BUSAB',
  'AKONT',
  'BEGRU',
  'KNRZE',
  'KNRZB',
  'ZAMIM',
  'ZAMIV',
  'ZAMIR',
  'ZAMIB',
  'ZAMIO',
  'ZWELS',
  'XVERR',
  'ZAHLS',
  'ZTERM',
  'WAKON',
  'VZSKZ',
  'ZINDT',
  'ZINRT',
  'EIKTO',
  'ZSABE',
  'KVERM',
  'FDGRV',
  'VRBKZ',
  'VLIBB',
  'VRSZL',
  'VRSPR',
  'VRSNR',
  'VERDT',
  'PERKZ',
  'XDEZV',
  'XAUSZ',
  'WEBTR',
  'REMIT',
  'DATLZ',
  'XZVER',
  'TOGRU',
  'KULTG',
  'HBKID',
  'XPORE',
  'BLNKZ',
  'ALTKN',
  'ZGRUP',
  'URLID',
  'MGRUP',
  'LOCKB',
  'UZAWE',
  'EKVBD',
  'SREGL',
  'XEDIP',
  'FRGRP',
  'VRSDG',
  'TLFXS',
  'INTAD',
  'XKNZB',
  'GUZTE',
  'GRICD',
  'GRIDT',
  'WBRSL',
  'CONFS',
  'UPDAT',
  'UPTIM',
  'NODEL',
  'TLFNS',
  'CESSION_KZ',
  'AVSND',
  'AD_HASH',
  'QLAND',
  'CVP_XBLCK_B',
  'CIIUCODE',
  'GMVKZD',
  'ZZTAX_DECL',
  'UPDATE_TS',
  '_operation_type',
] as const

export const importSapCustomerCompanySchema = z.object({
  KUNNR: sapNumberKeySchema,
  BUKRS: sapStringSchema,
  MANDT: sapStringSchema,
  AKONT: sapStringSchema,
  ZTERM: sapStringSchema,
  ZWELS: sapStringSchema,
  ZUAWA: sapStringSchema,
  FDGRV: sapStringSchema,
  ZSABE: sapStringSchema,
  EIKTO: sapStringSchema,
  ALTKN: sapStringSchema,
  XAUSZ: sapStringSchema,
  XZVER: sapBooleanSchema,
  TOGRU: sapStringSchema,
  INTAD: sapStringSchema,
  TLFNS: sapStringSchema,
  TLFXS: sapStringSchema,
  ERDAT: sapDateSchema,
  ERNAM: sapStringSchema,
})
export type ImportSapCustomerCompanyRecord = z.infer<typeof importSapCustomerCompanySchema>

/* ------------------------------------------------------------------ */
/* KNVP — Customer Partner Functions                                   */
/* ------------------------------------------------------------------ */

export const importSapPartnerFunctionsHeaders = [
  'MANDT',
  'KUNNR',
  'VKORG',
  'VTWEG',
  'SPART',
  'PARVW',
  'PARZA',
  'KUNN2',
  'LIFNR',
  'PERNR',
  'PARNR',
  'KNREF',
  'DEFPA',
  'ZZKNZNM',
  'UPDATE_TS',
  '_operation_type',
] as const

export const importSapPartnerFunctionsSchema = z.object({
  KUNNR: sapNumberKeySchema,
  VKORG: sapStringSchema,
  VTWEG: sapStringSchema,
  SPART: sapStringSchema,
  PARVW: sapStringSchema,
  PARZA: sapStringSchema,
  MANDT: sapStringSchema,
  KUNN2: sapNumberKeySchema,
  LIFNR: sapNumberKeySchema,
  PARNR: sapStringSchema,
})
export type ImportSapPartnerFunctionsRecord = z.infer<typeof importSapPartnerFunctionsSchema>

/* ------------------------------------------------------------------ */
/* VBAK — Sales Document Header                                        */
/* ------------------------------------------------------------------ */

export const importSapSalesOrdersHeaders = [
  'HK_H_ECC_VBAK',
  'FILE_ROW_NUMBER',
  'CDC_TIMESTAMP',
  'MANDT',
  'VBELN',
  'ERDAT',
  'ERZET',
  'ERNAM',
  'ANGDT',
  'BNDDT',
  'AUDAT',
  'VBTYP',
  'TRVOG',
  'AUART',
  'AUGRU',
  'GWLDT',
  'SUBMI',
  'LIFSK',
  'FAKSK',
  'NETWR',
  'WAERK',
  'VKORG',
  'VTWEG',
  'SPART',
  'VKGRP',
  'VKBUR',
  'GSBER',
  'GSKST',
  'GUEBG',
  'GUEEN',
  'KNUMV',
  'VDATU',
  'VPRGR',
  'AUTLF',
  'VBKLA',
  'VBKLT',
  'KALSM',
  'VSBED',
  'FKARA',
  'AWAHR',
  'KTEXT',
  'BSTNK',
  'BSARK',
  'BSTDK',
  'BSTZD',
  'IHREZ',
  'BNAME',
  'TELF1',
  'MAHZA',
  'MAHDT',
  'KUNNR',
  'KOSTL',
  'STAFO',
  'STWAE',
  'AEDAT',
  'KVGR1',
  'KVGR2',
  'KVGR3',
  'KVGR4',
  'KVGR5',
  'KNUMA',
  'KOKRS',
  'PS_PSP_PNR',
  'KURST',
  'KKBER',
  'KNKLI',
  'GRUPP',
  'SBGRP',
  'CTLPC',
  'CMWAE',
  'CMFRE',
  'CMNUP',
  'CMNGV',
  'AMTBL',
  'HITYP_PR',
  'ABRVW',
  'ABDIS',
  'VGBEL',
  'OBJNR',
  'BUKRS_VF',
  'TAXK1',
  'TAXK2',
  'TAXK3',
  'TAXK4',
  'TAXK5',
  'TAXK6',
  'TAXK7',
  'TAXK8',
  'TAXK9',
  'XBLNR',
  'ZUONR',
  'VGTYP',
  'KALSM_CH',
  'AGRZR',
  'AUFNR',
  'QMNUM',
  'VBELN_GRP',
  'SCHEME_GRP',
  'ABRUF_PART',
  'ABHOD',
  'ABHOV',
  'ABHOB',
  'RPLNR',
  'VZEIT',
  'STCEG_L',
  'LANDTX',
  'XEGDR',
  'ENQUEUE_GRP',
  'DAT_FZAU',
  'FMBDAT',
  'VSNMR_V',
  'HANDLE',
  'PROLI',
  'CONT_DG',
  'CRM_GUID',
  'UPD_TMSTMP',
  'MSR_ID',
  'TM_CTRL_KEY',
  'HANDOVERLOC',
  '_DATAAGING',
  'PSM_BUDAT',
  'FSH_KVGR6',
  'FSH_KVGR7',
  'FSH_KVGR8',
  'FSH_KVGR9',
  'FSH_KVGR10',
  'FSH_REREG',
  'FSH_CQ_CHECK',
  'FSH_VRSN_STATUS',
  'FSH_TRANSACTION',
  'FSH_VAS_CG',
  'FSH_CANDATE',
  'FSH_SS',
  'FSH_OS_STG_CHANGE',
  'SWENR',
  'SMENR',
  'PHASE',
  'MTLAUR',
  'STAGE',
  'HB_CONT_REASON',
  'HB_EXPDATE',
  'HB_RESDATE',
  'MILL_APPL_ID',
  'TAS',
  'BETC',
  'MOD_ALLOW',
  'CANCEL_ALLOW',
  'PAY_METHOD',
  'BPN',
  'REP_FREQ',
  'LOGSYSB',
  'KALCD',
  'MULTI',
  'SPPAYM',
  'WTYSC_CLM_HDR',
  'ZZT_3PL_ROUTE',
  '_DATAAGING_SIMP_DT',
  'DB_CUID',
  'HANA_ROWID',
  'ABHOB_SIMP_TM',
  'ABHOD_SIMP_DT',
  'ABHOV_SIMP_TM',
  'AEDAT_SIMP_DT',
  'ANGDT_SIMP_DT',
  'AUDAT_SIMP_DT',
  'BNDDT_SIMP_DT',
  'BSTDK_SIMP_DT',
  'CMFRE_SIMP_DT',
  'CMNGV_SIMP_DT',
  'CMNUP_SIMP_DT',
  'DAT_FZAU_SIMP_DT',
  'ERDAT_SIMP_DT',
  'ERZET_SIMP_TM',
  'FMBDAT_SIMP_DT',
  'FSH_CANDATE_SIMP_DT',
  'GUEBG_SIMP_DT',
  'GUEEN_SIMP_DT',
  'GWLDT_SIMP_DT',
  'HB_EXPDATE_SIMP_DT',
  'HB_RESDATE_SIMP_DT',
  'MAHDT_SIMP_DT',
  'PSM_BUDAT_SIMP_DT',
  'VDATU_SIMP_DT',
  'VZEIT_SIMP_TM',
  'DSS_CHANGE_HASH',
  'FILE_NAME',
  'INSERT_TS',
  'EFFECTIVE_START_DATE',
  'DSS_VERSION',
  'UPDATE_TS',
] as const

export const importSapSalesOrdersSchema = z.object({
  VBELN: sapStringSchema,
  MANDT: sapStringSchema,
  AUART: sapStringSchema,
  VBTYP: sapStringSchema,
  TRVOG: sapStringSchema,
  AUGRU: sapStringSchema,
  ERDAT: sapDateSchema,
  ERZET: sapStringSchema,
  ERNAM: sapStringSchema,
  AUDAT: sapDateSchema,
  AEDAT: sapDateSchema,
  ANGDT: sapDateSchema,
  BNDDT: sapDateSchema,
  GWLDT: sapDateSchema,
  GUEBG: sapDateSchema,
  GUEEN: sapDateSchema,
  VDATU: sapDateSchema,
  NETWR: sapNumericStringSchema,
  WAERK: sapStringSchema,
  VKORG: sapStringSchema,
  VTWEG: sapStringSchema,
  SPART: sapStringSchema,
  BUKRS_VF: sapStringSchema,
  KOKRS: sapStringSchema,
  KUNNR: sapNumberKeySchema,
  TELF1: sapStringSchema,
  BSTNK: sapStringSchema,
  BSARK: sapStringSchema,
  BSTDK: sapDateSchema,
  IHREZ: sapStringSchema,
  KNUMV: sapStringSchema,
  KALSM: sapStringSchema,
  VSBED: sapStringSchema,
  AWAHR: sapIntegerSchema,
  KVGR1: sapStringSchema,
  KVGR2: sapStringSchema,
  KVGR3: sapStringSchema,
  KVGR4: sapStringSchema,
  KVGR5: sapStringSchema,
  KKBER: sapStringSchema,
  KNKLI: sapNumberKeySchema,
  CTLPC: sapStringSchema,
  CMWAE: sapStringSchema,
  AMTBL: sapNumericStringSchema,
  CMFRE: sapDateSchema,
  TAXK1: sapStringSchema,
  TAXK2: sapStringSchema,
  VGBEL: sapStringSchema,
  VGTYP: sapStringSchema,
  LANDTX: sapStringSchema,
})
export type ImportSapSalesOrdersRecord = z.infer<typeof importSapSalesOrdersSchema>

/* ------------------------------------------------------------------ */
/* VBRK — Billing Document Header Data                                 */
/* ------------------------------------------------------------------ */

export const importSapBillingDocumentsHeaders = [
  'HK_H_ECC_VBRK',
  'MANDT',
  'VBELN',
  'FKART',
  'FKTYP',
  'VBTYP',
  'WAERK',
  'VKORG',
  'VTWEG',
  'KALSM',
  'KNUMV',
  'VSBED',
  'FKDAT',
  'BELNR',
  'GJAHR',
  'POPER',
  'KONDA',
  'KDGRP',
  'BZIRK',
  'PLTYP',
  'INCO1',
  'INCO2',
  'EXPKZ',
  'RFBSK',
  'MRNKZ',
  'KURRF',
  'CPKUR',
  'VALTG',
  'VALDT',
  'ZTERM',
  'ZLSCH',
  'KTGRD',
  'LAND1',
  'REGIO',
  'COUNC',
  'CITYC',
  'BUKRS',
  'TAXK1',
  'TAXK2',
  'TAXK3',
  'TAXK4',
  'TAXK5',
  'TAXK6',
  'TAXK7',
  'TAXK8',
  'TAXK9',
  'NETWR',
  'ZUKRI',
  'ERNAM',
  'ERZET',
  'ERDAT',
  'STAFO',
  'KUNRG',
  'KUNAG',
  'MABER',
  'STWAE',
  'EXNUM',
  'STCEG',
  'AEDAT',
  'SFAKN',
  'KNUMA',
  'FKART_RL',
  'FKDAT_RL',
  'KURST',
  'MSCHL',
  'MANSP',
  'SPART',
  'KKBER',
  'KNKLI',
  'CMWAE',
  'CMKUF',
  'HITYP_PR',
  'BSTNK_VF',
  'VBUND',
  'FKART_AB',
  'KAPPL',
  'LANDTX',
  'STCEG_H',
  'STCEG_L',
  'XBLNR',
  'ZUONR',
  'MWSBK',
  'LOGSYS',
  'FKSTO',
  'XEGDR',
  'RPLNR',
  'LCNUM',
  'J_1AFITP',
  'KURRF_DAT',
  'AKWAE',
  'AKKUR',
  'KIDNO',
  'BVTYP',
  'NUMPG',
  'BUPLA',
  'VKONT',
  'FKK_DOCSTAT',
  'NRZAS',
  'SPE_BILLING_IND',
  'VTREF',
  'FK_SOURCE_SYS',
  'FKTYP_CRM',
  'STGRD',
  'DPC_REL',
  'MNDID',
  'PAY_TYPE',
  'SEPON',
  'MNDVG',
  'SPPAYM',
  'SPPORD',
  'ZZT_3PL_ROUTE',
  'ZZT_ORDER_STATUS',
  'ZZT_CANCEL_FOR',
  'AEDAT_SIMP_DT',
  'ERDAT_SIMP_DT',
  'ERZET_SIMP_TM',
  'FKDAT_SIMP_DT',
  'FKDAT_RL_SIMP_DT',
  'KURRF_DAT_SIMP_DT',
  'VALDT_SIMP_DT',
  'DB_CUID',
  'CDC_TIMESTAMP',
  'DSS_CHANGE_HASH',
  'EFFECTIVE_START_DATE',
  'DSS_VERSION',
  'UPDATE_TS',
  'FILE_NAME',
  'FILE_ROW_NUMBER',
  'INSERT_TS',
] as const

export const importSapBillingDocumentsSchema = z.object({
  VBELN: sapStringSchema,
  MANDT: sapStringSchema,
  FKART: sapStringSchema,
  FKTYP: sapStringSchema,
  VBTYP: sapStringSchema,
  WAERK: sapStringSchema,
  VKORG: sapStringSchema,
  VTWEG: sapStringSchema,
  SPART: sapStringSchema,
  KALSM: sapStringSchema,
  KNUMV: sapStringSchema,
  VSBED: sapStringSchema,
  FKDAT: sapDateSchema,
  KDGRP: sapStringSchema,
  BZIRK: sapStringSchema,
  INCO1: sapStringSchema,
  INCO2: sapStringSchema,
  RFBSK: sapStringSchema,
  KURRF: sapStringSchema,
  KURRF_DAT: sapDateSchema,
  ZTERM: sapStringSchema,
  KTGRD: sapStringSchema,
  LAND1: sapStringSchema,
  REGIO: sapStringSchema,
  BUKRS: sapStringSchema,
  TAXK1: sapStringSchema,
  TAXK2: sapStringSchema,
  LANDTX: sapStringSchema,
  XBLNR: sapStringSchema,
  ZUONR: sapStringSchema,
  KUNAG: sapNumberKeySchema,
  KUNRG: sapNumberKeySchema,
  ERDAT: sapDateSchema,
  ERZET: sapStringSchema,
  ERNAM: sapStringSchema,
})
export type ImportSapBillingDocumentsRecord = z.infer<typeof importSapBillingDocumentsSchema>

/* ------------------------------------------------------------------ */
/* VBRP — Billing Document Item Data (billing document details)        */
/* ------------------------------------------------------------------ */

/** The finance team's trimmed extract, in file order — a header row is skipped by the worker. */
export const importSapBillingDocumentDetailsHeaders = [
  'VBELN',
  'AUBEL',
  'VGBEL',
  'WERKS',
  'MATNR',
  'ARKTX',
  'ERDAT',
  'FKIMG',
  'VRKME',
  'NETWR',
  'MWSBP',
] as const

/**
 * `VBELN` / `AUBEL` are cleaned like `customer_transactions.billing_document` and
 * `sales_order_headers.sales_document` (`sapStringSchema`: trim only, no leading-zero strip), so
 * the equality joins between the three tables hold. Dates are MM/DD/YYYY; amounts may carry comma
 * thousands separators.
 */
export const importSapBillingDocumentDetailsSchema = z.object({
  VBELN: sapStringSchema,
  AUBEL: sapStringSchema,
  VGBEL: sapStringSchema,
  WERKS: sapStringSchema,
  MATNR: sapStringSchema,
  ARKTX: sapStringSchema,
  ERDAT: sapUsDateSchema,
  FKIMG: sapNumericStringSchema,
  VRKME: sapStringSchema,
  NETWR: sapNumericStringSchema,
  MWSBP: sapNumericStringSchema,
})
export type ImportSapBillingDocumentDetailsRecord = z.infer<
  typeof importSapBillingDocumentDetailsSchema
>

/* ------------------------------------------------------------------ */
/* FBL5N — Customer Line Item List (customer transactions)             */
/* ------------------------------------------------------------------ */

export const importSapCustomerTransactionsHeaders = [
  'payment_status',
  'business_name',
  'payer_erp',
  'sold_to_erp',
  'payment_block',
  'type',
  'Reference',
  'order_payment_terms',
  'text',
  'billing_document',
  'document_number',
  'clearing_document_number',
  'gl_number',
  'document_header_text',
  'Assignment',
  'value',
  'Arrear',
  'document_date',
  'net_due_date',
  'clearing_date',
] as const

export const importSapCustomerTransactionsSchema = z.object({
  payment_status: sapIconLabelSchema,
  business_name: sapStringSchema,
  payer_erp: sapNumberKeySchema,
  sold_to_erp: sapNumberKeySchema,
  payment_block: sapStringSchema,
  type: sapStringSchema,
  Reference: sapStringSchema,
  order_payment_terms: sapStringSchema,
  text: sapStringSchema,
  billing_document: sapStringSchema,
  document_number: sapStringSchema,
  clearing_document_number: sapStringSchema,
  gl_number: sapStringSchema,
  document_header_text: sapStringSchema,
  Assignment: sapStringSchema,
  value: sapNumericStringSchema,
  Arrear: sapIntegerSchema,
  document_date: sapUsDateSchema,
  net_due_date: sapUsDateSchema,
  clearing_date: sapUsDateSchema,
})
export type ImportSapCustomerTransactionsRecord = z.infer<
  typeof importSapCustomerTransactionsSchema
>

/* ------------------------------------------------------------------ */
/* BP — Customer Partner Credit Segments                               */
/* ------------------------------------------------------------------ */

/**
 * The raw extract has 67 columns but only these 8 are meaningful. The file is trimmed to these 8
 * columns (in this order) before upload, so the header list matches it 1:1.
 */
export const importSapCustomerPartnerCreditSegmentsHeaders = [
  'erp',
  'business_name',
  'credit_segment',
  'is_credit_blocked',
  'current_risk_class',
  'current_credit_limit',
  'next_review_date',
  'last_review_date',
] as const

/**
 * `erp` must be numeric after stripping leading zeros; blank/non-numeric rows are rejected (tracked
 * as invalid, never inserted). This also guards against a stray header row slipping through.
 */
const bpErpSchema = z
  .string()
  .nullable()
  .default(null)
  .transform((value) => (value ?? '').trim().replace(/^0+/, ''))
  .refine((value) => /^\d+$/.test(value))

export const importSapCustomerPartnerCreditSegmentsSchema = z.object({
  erp: bpErpSchema,
  business_name: sapStringSchema,
  credit_segment: sapStringSchema,
  is_credit_blocked: sapBooleanSchema,
  current_risk_class: sapStringSchema,
  current_credit_limit: sapNumericStringSchema,
  next_review_date: sapUsDateSchema,
  last_review_date: sapUsDateSchema,
})
export type ImportSapCustomerPartnerCreditSegmentsRecord = z.infer<
  typeof importSapCustomerPartnerCreditSegmentsSchema
>

/* ------------------------------------------------------------------ */
/* Field labels: raw SAP column -> our interpreted (snake_case) name   */
/* Used by the upload report to show both names in the column header.  */
/* ------------------------------------------------------------------ */

const importSapCustomerFieldLabels: Record<string, string> = {
  KUNNR: 'customer_number',
  MANDT: 'sap_client',
  NAME1: 'name',
  NAME2: 'name_2',
  NAME4: 'name_4',
  STRAS: 'street',
  ORT01: 'city',
  ORT02: 'district',
  REGIO: 'region',
  PSTLZ: 'postal_code',
  LAND1: 'country',
  SORTL: 'search_term',
  TELF1: 'phone',
  TELF2: 'phone_2',
  TELFX: 'fax',
  ADRNR: 'address_number',
  KTOKD: 'account_group',
  KUKLA: 'customer_class',
  BRSCH: 'industry',
  SPRAS: 'language',
  LIFNR: 'vendor_number',
  AUFSD: 'central_order_block',
  BEGRU: 'authorization_group',
  STCD1: 'tax_number_1',
  STCD3: 'tax_number_3',
  LZONE: 'transport_zone',
  TXJCD: 'tax_jurisdiction',
  ERDAT: 'created_on',
  ERNAM: 'created_by_user',
}

const importSapCustomerCompanyFieldLabels: Record<string, string> = {
  KUNNR: 'customer_number',
  BUKRS: 'company_code',
  MANDT: 'sap_client',
  AKONT: 'reconciliation_account',
  ZTERM: 'payment_terms',
  ZWELS: 'payment_methods',
  ZUAWA: 'sort_key',
  FDGRV: 'cash_management_group',
  ZSABE: 'accounting_clerk',
  EIKTO: 'account_number_at_customer',
  ALTKN: 'previous_account_number',
  XAUSZ: 'account_statement_indicator',
  XZVER: 'record_payment_history',
  TOGRU: 'tolerance_group',
  INTAD: 'clerk_email',
  TLFNS: 'clerk_phone',
  TLFXS: 'clerk_fax',
  ERDAT: 'created_on',
  ERNAM: 'created_by_user',
}

const importSapPartnerFunctionsFieldLabels: Record<string, string> = {
  KUNNR: 'customer_number',
  VKORG: 'sales_organization',
  VTWEG: 'distribution_channel',
  SPART: 'division',
  PARVW: 'partner_function',
  PARZA: 'partner_counter',
  MANDT: 'sap_client',
  KUNN2: 'payer_erp',
  LIFNR: 'partner_vendor_number',
  PARNR: 'contact_person_number',
}

const importSapSalesOrdersFieldLabels: Record<string, string> = {
  VBELN: 'sales_document',
  MANDT: 'sap_client',
  AUART: 'sales_document_type',
  VBTYP: 'sales_and_distribution_document_category',
  TRVOG: 'transaction_group',
  AUGRU: 'order_reason',
  ERDAT: 'created_on',
  ERZET: 'created_time',
  ERNAM: 'created_by_user',
  AUDAT: 'document_date',
  AEDAT: 'last_changed_on',
  ANGDT: 'quotation_valid_from',
  BNDDT: 'quotation_binding_date',
  GWLDT: 'warranty_date',
  GUEBG: 'validity_from',
  GUEEN: 'validity_to',
  VDATU: 'requested_delivery_date',
  NETWR: 'net_value',
  WAERK: 'currency',
  VKORG: 'sales_organization',
  VTWEG: 'distribution_channel',
  SPART: 'division',
  BUKRS_VF: 'company_code',
  KOKRS: 'controlling_area',
  KUNNR: 'sold_to_party',
  TELF1: 'contact_phone',
  BSTNK: 'customer_purchase_order_number',
  BSARK: 'purchase_order_type',
  BSTDK: 'customer_purchase_order_date',
  IHREZ: 'your_reference',
  KNUMV: 'conditions_number',
  KALSM: 'pricing_procedure',
  VSBED: 'shipping_conditions',
  AWAHR: 'order_probability',
  KVGR1: 'customer_group_1',
  KVGR2: 'customer_group_2',
  KVGR3: 'customer_group_3',
  KVGR4: 'customer_group_4',
  KVGR5: 'customer_group_5',
  KKBER: 'credit_control_area',
  KNKLI: 'credit_account',
  CTLPC: 'credit_risk_category',
  CMWAE: 'credit_currency',
  AMTBL: 'released_credit_value',
  CMFRE: 'credit_release_date',
  TAXK1: 'tax_classification_1',
  TAXK2: 'tax_classification_2',
  VGBEL: 'reference_sales_and_distribution_document',
  VGTYP: 'reference_document_category',
  LANDTX: 'tax_departure_country',
}

const importSapBillingDocumentsFieldLabels: Record<string, string> = {
  VBELN: 'billing_document',
  MANDT: 'sap_client',
  FKART: 'billing_type',
  FKTYP: 'billing_category',
  VBTYP: 'sales_and_distribution_document_category',
  WAERK: 'currency',
  VKORG: 'sales_organization',
  VTWEG: 'distribution_channel',
  SPART: 'division',
  KALSM: 'pricing_procedure',
  KNUMV: 'conditions_number',
  VSBED: 'shipping_conditions',
  FKDAT: 'billing_date',
  KDGRP: 'customer_group',
  BZIRK: 'sales_district',
  INCO1: 'incoterms_1',
  INCO2: 'incoterms_2',
  RFBSK: 'posting_status',
  KURRF: 'exchange_rate',
  KURRF_DAT: 'exchange_rate_date',
  ZTERM: 'payment_terms',
  KTGRD: 'account_assignment_group',
  LAND1: 'country',
  REGIO: 'region',
  BUKRS: 'company_code',
  TAXK1: 'tax_classification_1',
  TAXK2: 'tax_classification_2',
  LANDTX: 'tax_departure_country',
  XBLNR: 'reference_document_number',
  ZUONR: 'assignment_number',
  KUNAG: 'sold_to_party',
  KUNRG: 'payer',
  ERDAT: 'created_on',
  ERZET: 'created_time',
  ERNAM: 'created_by_user',
}

const importSapBillingDocumentDetailsFieldLabels: Record<string, string> = {
  VBELN: 'billing_document',
  AUBEL: 'sales_document',
  VGBEL: 'reference_sales_and_distribution_document',
  WERKS: 'plant',
  MATNR: 'material',
  ARKTX: 'material_description',
  ERDAT: 'created_on',
  FKIMG: 'billed_quantity',
  VRKME: 'sales_unit',
  NETWR: 'net_value',
  MWSBP: 'tax_amount',
}

const importSapCustomerTransactionsFieldLabels: Record<string, string> = {
  payment_status: 'payment_status',
  business_name: 'business_name',
  payer_erp: 'payer_erp',
  sold_to_erp: 'sold_to_erp',
  payment_block: 'payment_block',
  type: 'type',
  Reference: 'reference',
  order_payment_terms: 'order_payment_terms',
  text: 'text',
  billing_document: 'billing_document',
  document_number: 'document_number',
  clearing_document_number: 'clearing_document_number',
  gl_number: 'gl_number',
  document_header_text: 'document_header_text',
  Assignment: 'assignment',
  value: 'value',
  Arrear: 'arrear',
  document_date: 'document_date',
  net_due_date: 'net_due_date',
  clearing_date: 'clearing_date',
}

const importSapCustomerPartnerCreditSegmentsFieldLabels: Record<string, string> = {
  erp: 'erp',
  business_name: 'business_name',
  credit_segment: 'credit_segment',
  is_credit_blocked: 'is_credit_blocked',
  current_risk_class: 'current_risk_class',
  current_credit_limit: 'current_credit_limit',
  next_review_date: 'next_review_date',
  last_review_date: 'last_review_date',
}

/** Per-upload-type maps of raw SAP column name to our interpreted (snake_case) column name. */
export const SAP_IMPORT_FIELD_COLUMN_MAP: Record<string, Record<string, string>> = {
  'import-sap-customer': importSapCustomerFieldLabels,
  'import-sap-customer-company': importSapCustomerCompanyFieldLabels,
  'import-sap-partner-functions': importSapPartnerFunctionsFieldLabels,
  'import-sap-sales-orders': importSapSalesOrdersFieldLabels,
  'import-sap-billing-documents': importSapBillingDocumentsFieldLabels,
  'import-sap-billing-document-details': importSapBillingDocumentDetailsFieldLabels,
  'import-sap-customer-transactions': importSapCustomerTransactionsFieldLabels,
  'import-sap-customer-partner-credit-segments': importSapCustomerPartnerCreditSegmentsFieldLabels,
}
